import { Component, ChangeDetectionStrategy, computed, inject, OnInit } from '@angular/core';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { HeaderComponent } from '../../ui/header/header.component';
import { TranslocoPipe } from '@jsverse/transloco';
import { filter, map, startWith } from 'rxjs';
import { RegionService } from '../../../../services/region.service';
import { BackofficeRoleService } from '../../../../services/backoffice-role.service';
import { resolveAccountOAuthConfig } from '../../../account/oauth-callback/oauth-callback.constants';

@Component({
  selector: 'app-base-layout',
  imports: [
    NzLayoutModule,
    NzIconModule,
    NzMenuModule,
    RouterOutlet,
    RouterLink,
    HeaderComponent,
    TranslocoPipe,
  ],
  templateUrl: './base-layout.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './base-layout.component.scss',
})
export class BaseLayoutComponent implements OnInit {
  isCollapsed = false;
  /** Remount page outlet when region changes so list/detail data cannot stay stale. */
  private readonly regionService = inject(RegionService);
  private readonly backofficeRoleService = inject(BackofficeRoleService);
  protected readonly regionEpoch = this.regionService.regionEpoch;
  protected readonly canManageRoles = this.backofficeRoleService.canManageRoles;
  /** 固件上传与元数据管理集中在主 region，其余 region 整个入口隐藏。 */
  protected readonly isPrimaryRegion = this.backofficeRoleService.isRoleAdminRegion;
  protected readonly accountProfileUrl = computed(() => {
    const cfg = resolveAccountOAuthConfig(this.regionService.regionCode());
    return `${cfg.accountAppUrl.replace(/\/$/, '')}/profile`;
  });

  private readonly router = inject(Router);

  async ngOnInit() {
    await this.backofficeRoleService.refreshFromToken();
  }

  /** 侧边栏菜单项 → 触发高亮的一组 URL 前缀（含详情页的兄弟路由）。 */
  private readonly menuHighlightPrefixes: Record<string, readonly string[]> = {
    siteList: ['/site'],
    deviceList: [
      '/device/list',
      '/device/batch-add',
      '/device/mpm',
      '/device/mpe',
      '/device/mpm-au',
      '/device/lbz-inverter',
      '/device/lbz-gw',
    ],
    deviceGroup: ['/device-group'],
    dongleList: ['/device/dongle-list', '/device/dongle'],
    deviceEvent: ['/device/event'],
    deviceBattery: ['/device/battery'],
    deviceModel: ['/device/model'],
    deviceFirmware: ['/device/firmware'],
    deviceGridCodes: ['/device/grid-codes'],
    deviceSgccCsrs: ['/device/sgcc-csrs'],
    deviceMqttData: ['/device/iot-mqtt-data-list'],
    ispList: ['/isp'],
    systemNotification: ['/system-status/notification-list'],
    systemLog: ['/system-status/audit-log-list'],
    accountUserList: ['/account-management/user'],
    accountRoleList: ['/account-management/role'],
  };

  /**
   * 当前 URL 命中的菜单项 key。按「section 前缀」匹配，而不是 nzMatchRouter 的
   * routerLink 前缀匹配，这样列表项进入详情页（如 /site/list → /site/dashboard）
   * 仍能保持对应菜单项高亮；未命中任何前缀时不高亮任何项。
   */
  protected readonly activeMenuKey = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.menuKeyOf(this.router.url)),
      startWith(this.menuKeyOf(this.router.url)),
    ),
    { initialValue: this.menuKeyOf(this.router.url) },
  );

  private menuKeyOf(url: string): string | null {
    const path = url.split('?')[0];
    for (const [key, prefixes] of Object.entries(this.menuHighlightPrefixes)) {
      if (prefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
        return key;
      }
    }
    return null;
  }
}
